<template>
  <div>
    <!-- Activities table -->
    <div class="card pagination-margin"
         qid="generic-controls-section"
         v-if="isGranted([permissions.MEASURE_VIEW, permissions.MEASURE_VIEW_OWN], measure)"
    >
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h5 class="float-left">{{ $t('maintenance.measures.generic_controls_title') }}</h5>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('activities.table.name') }}
            </th>
            <th scope="col">
              {{ $t('generic_controls.table.control_type') }}
            </th>
            <th scope="col">
              {{ $t('generic_controls.table.type') }}
            </th>
            <th scope="col">
              {{ $t('system.latest_status') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="object in tableItems">
            <td>
              <router-link
                  :to="{ name: 'generic_controls.view', params: { slug: object.generic_control.slug }}"
                  qid="s-view-page-link"
                  v-if="object.generic_control.client == getClient.slug"
              >
                {{ object.generic_control.name }}
              </router-link>
              <button v-if="object.generic_control.client != getClient.slug"
                      class="btn btn-link btn-sm"
                      @click="openNoticeModal(object.generic_control)"
              >
                {{ object.generic_control.name }}
              </button>
            </td>
            <td>
              <span v-if="object.generic_control.type" >
                <span class="badge">
                  <font-awesome-icon icon="shield-alt"/>
                  {{ $t('generic_controls.category.' + $options.filters.slugify(object.generic_control.type)) }}
                </span>
              </span>
            </td>
            <td>
              <span class="mr-1" qid="genericControl-tags-list" v-if="object.generic_control.control_types && object.generic_control.control_types.length">
                <span class="mr-1" v-for="type in object.generic_control.control_types" qid="p-tags-list">
                  <span class="badge">
                    <font-awesome-icon icon="shapes"/>
                    {{ $t('generic_controls.control_type.' + $options.filters.slugify(type)) }}
                  </span>
                </span>
              </span>
            </td>
            <td>
              <span v-if="object.generic_control.last_performed_all" class="text-normal">
                <ControlResultBadge v-if="['completed', 'deviations', 'not_performed', 'not_applicable', 'pass', 'fail'].includes(object.generic_control.last_performed_all.completion_status)" :value="object.generic_control.last_performed_all.completion_status"></ControlResultBadge>
                <span v-else>
                  <font-awesome-icon
                          icon="exclamation-triangle"
                          class="risk-0"
                  />
                  {{ $t('system.not_performed')}}
                </span>
              </span>
              <span v-else class="text-normal">
                <font-awesome-icon
                        icon="exclamation-triangle"
                        class="risk-0"
                />
                {{ $t('system.not_performed')}}
              </span>
            </td>
            <td>
              <span class="text-normal">
                {{ object.generic_control.updated_at | formatDate }}
              </span>
            </td>
          </tr>
          <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
            <td colspan="10">
              <h5 class="mt-3">
                {{ $t('system.no_results') }}
              </h5>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="card-body">
        <div class="pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                        v-model="perPage"
                        :options="perPageOptions"
                        track-by="value"
                        label="value"
                        :select-label="$t('system.dropdown_select_label')"
                        :selected-label="$t('system.dropdown_selected_label')"
                        :deselect-label="$t('system.dropdown_deselect_label')"
                        qid="create-org-form-organization-level"
                        @select="perPageChanged"
                        :placeholder="selectedPerPage"
                        @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                    class="float-right"
                    v-model="currentPage"
                    :total-rows="rows"
                    :per-page="selectedPerPage"
                    aria-controls="my-table"
                    @change="pageChanged"
                    limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete system modal template -->
    <b-modal ref="delete-ac" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ac-title">
          {{ $t('activities.system.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.system.delete.description') }}
        <span v-if="itemToDelete">{{ itemToDelete.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.system.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideSConfirmDeleteModal"
                  qid="delete-ac-cancel-button"
        >
          {{ $t('activities.system.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ac-submit-button"
        >
          {{ $t('activities.system.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

    <PartitionNoticeModal
        :item="selectedItem"
        state="generic_controls.view"
        :show="showNoticeModal"
        @closeViewModal="closeNoticeModal"
    ></PartitionNoticeModal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import PartitionNoticeModal from '@/components/PartitionNoticeModal.vue';


  export default {
    name: 'ActivitiesTable',
    data: function() {
      return {
        sForm: {
          items: []
        },

        measure: {},

        // Systems
        tableItems: [],
        selectedS: [],
        viewSModal: false,
        selectedSSort: '',
        selectedSDirection: '',
        sItems: [],
        sLoading: true,
        selectedItem: {},

        // Data objects
        allDataObjects: [],
        allDataObjectsFiltered: [],
        dataObjects: [],
        systemDataObjects: [],
        newDataObjects: [],
        DOItems: [],
        selectedDataObjects: [],
        availableDataObjects: [],

        error: '',
        loadingItems: false,
        itemToDelete: {},
        itemObject: {},
        selectedP: [],
        selectedUsers: [],
        users: [],
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        buttonDisabled: false,
        keywordValue: '',
        keywordFilterValue: '',
        showSelectionTable: false,
        selectedFiles: [],
        inputLink: '',
        showDocuments: false,
        filteredDataObjects: [],
        showUsersModal: false,
        showTagsModal: false,
        open_tasks: [],
        showListOfTasks: false,

        currentPage: 1,
        totalRows: 0,
        loading: true,
        perPage: '',
        selectedPerPage: "10",
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' },
        ],
        showNoticeModal: false,
        selectedItem: {}
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      }
    },
    components: {
      Avatar,
      ShowMoreModal,
      PartitionNoticeModal
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFiles() {
        return this.selectedFiles
      },
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when prop changes and then load validations
      object: function (value) {
        this.loadContent()
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadDO(this.keywordValue)
      }, 400),
      resetFile() {
        let imagefile = document.querySelector('#file');
        imagefile.value = '';
      },
      onFileChange() {
        let files = document.querySelector('#file');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      openNoticeModal(object) {
        this.selectedItem = object
        this.showNoticeModal = true
      },
      closeNoticeModal() {
        this.selectedItem = {}
        this.showNoticeModal = false
      },
      handleLink() {
        // If there's a link in input, add it to links array
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.sForm.links.push(this.inputLink)
          // Reset link input form
          this.inputLink = ''
        }
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.sForm.links.splice(index, 1);
      },
      addData() {
        let self = this;
        _.forEach(this.DOItems, function (item) {
          if (item.selected) {
            self.updateDOList(item)
          }
        })
        this.keywordValue = '';
        this.loadDO()
      },
      filterSelectedData() {
        let self = this;
        this.allDataObjectsFiltered = [];
        if (self.keywordFilterValue) {
          _.forEach(this.allDataObjects, function (item) {
            if (
              item.name.toLowerCase().indexOf(self.keywordFilterValue.toLowerCase()) !== -1 ||
              (item.category && item.category.toLowerCase().indexOf(self.keywordFilterValue.toLowerCase()) !== -1) ||
              (item.classification && item.classification.toLowerCase().indexOf(self.keywordFilterValue.toLowerCase()) !== -1)
            ) {
              self.allDataObjectsFiltered.push(item)
            }
          })
        } else {
          this.allDataObjectsFiltered = _.cloneDeep(this.allDataObjects);
        }
      },
      resetFields() {
        this.selectedRC = null
        this.selectedP = null
      },
      showDataObjectsModal(object, classification) {
        let self = this;
        this.selectedS = object

        if (!object) {
          this.availableDataObjects = [];
        }

        _.forEach(object.data_objects, function (item) {
          if (item.classification == classification) {
            self.filteredDataObjects.push(item)
          }
        })

        this.$refs['view-data-objects'].show()
      },
      hideDataObjectsModal() {
        this.selectedS = {}
        this.filteredDataObjects = []
        this.$refs['view-data-objects'].hide()
      },
      showEditModal(object) {
        let self = this;
        this.error = '';
        this.loadUsers();

        if (object.users && object.users.length) {
          self.selectedUsers = [];
          _.forEach(object.users, function (user) {
              self.selectedUsers.push(user)
          })
        }

        this.selectedS = object
        this.$refs['edit-item'].show()
      },
      hideEditModal() {
        this.selectedS = {}
        this.selectedUsers = [],
        this.$refs['edit-item'].hide()
      },
      showSConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.showListOfTasks = false
        this.$refs['delete-ac'].show()
      },
      hideSConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-ac'].hide()
      },
      openTagsModal(object, evt) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      getReducedUsers(users, type) {
        let reducedUsers = []

        if (type == 'owner') {
          _.forEach(users, function (item) {
            if (item.owner) {
              reducedUsers.push(item)
            }
          })
        } else if (type == 'member') {
          _.forEach(users, function (item) {
            if (!item.owner) {
              reducedUsers.push(item)
            }
          })
        } else {
          if (users.length > 3) {
            reducedUsers = _.cloneDeep(users);
            reducedUsers = reducedUsers.slice(0, 3);
          } else {
            reducedUsers = users;
          }
        }

        return reducedUsers;
      },
      showDataObjects(object) {
        this.availableDataObjects = []
        if (object && object.data_objects && object.data_objects.length) {
          this.availableDataObjects = object.data_objects;
        }
      },
      hideDataObjects() {
        this.availableDataObjects = []
      },
      handleDO(index, item) {
        // Toggle related/not related property
        item.related = item.related ? false : true;
        this.$set(this.allDataObjects, index, item)
      },
      selectDataObject(index, item) {
        // Toggle related/not related property
        item.selected = item.selected ? false : true;
        this.$set(this.DOItems, index, item)
      },
      selectAll() {
        let newDataObjectsArray = []
        let self = this;
        _.forEach(this.DOItems, function (item) {
          item.selected = true;
          newDataObjectsArray.push(item)
        })
        this.DOItems = newDataObjectsArray;
      },
      unselectAll() {
        let newDataObjectsArray = []
        let self = this;
        _.forEach(this.DOItems, function (item) {
          item.selected = false;
          newDataObjectsArray.push(item)
        })
        this.DOItems = newDataObjectsArray;
      },
      relateAll() {
        let newDataObjectsArray = []
        _.forEach(this.allDataObjects, function (item) {
          item.related = true;
          newDataObjectsArray.push(item)
        })
        // make cloned array to avoid double actions on arrays
        this.allDataObjects = _.cloneDeep(newDataObjectsArray);
        this.allDataObjectsFiltered = _.cloneDeep(newDataObjectsArray);
      },
      unrelateAll() {
        let newDataObjectsArray = []
        _.forEach(this.allDataObjects, function (item) {
          item.related = false;
          newDataObjectsArray.push(item)
        })
        // make cloned array to avoid double actions on arrays
        this.allDataObjects = _.cloneDeep(newDataObjectsArray);
        this.allDataObjectsFiltered = _.cloneDeep(newDataObjectsArray);
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('measures/' + this.$route.params.slug + '/generic-controls/' + object.id).then(function() {
          self.hideSConfirmDeleteModal();
          self.loadGenericControls();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('projects.generic_controls.delete.success'))
        }, function() {});
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;

        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      loadContent(page, perPage) {
        let self = this;

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        if (this.object && this.object.id) {
          piincHttp.get('policies/' + this.object.slug + '/generic-controls', { params:
                    {
                      page: pageNumber,
                      per_page: perPageNumber,
                      client: this.getClient.slug
                    }
          }).then(function (response) {
            self.loading = false;
            self.tableItems = response.data.items;
            // Set number of total items
            self.totalRows = response.data.total_count;
          }, function () {
          });
        }
      }
    },
    created () {
      this.measure = this.object;
      // Table items
      this.loadContent();
    }
  }
</script>
